/* eslint-disable @typescript-eslint/no-explicit-any */
//
// compat-client.ts
//
// This file contains a client-side compatibility layer for Clerk's Next.js SDK.
// It's designed to solve the problem of changing user and organization IDs when
// migrating from a development to a production Clerk instance.
//
// This compatibility layer intercepts the `useAuth()` hook and uses the
// `external_id` from the user's and organization's public metadata as the
// primary ID. The migration script is responsible for setting this `external_id`
// to the user's or organization's original ID from the development instance.
//
// For this to work, you MUST configure a custom session token template in your
// Clerk dashboard. See the "Sessions" page in your Clerk dashboard for more info.
//
import { useAuth as useOriginalAuth, useSession } from '@clerk/nextjs';

/**
 * A wrapper around Clerk's `useAuth()` hook that prioritizes `external_id`
 * from the session token claims. This allows for a seamless migration from a
 * development to a production Clerk instance without changing all the user
 * and organization IDs in your database.
 *
 * @returns An auth object with `userId` and `orgId` set to the `external_id`
 *          if available, otherwise the original Clerk ID.
 */
export function useAuth() {
  const auth = useOriginalAuth();
  const { session } = useSession();

  if (session) {
    // Access the custom claims from the experimental property.
    // This requires a JWT template to be configured in the Clerk dashboard.
    const customClaims = (session as any).__experimental_sessionClaims;
    if (customClaims) {
      const userId = customClaims?.userId || auth.userId;
      const orgId = customClaims?.orgId || auth.orgId;
      return { ...auth, userId, orgId };
    }
  }

  return auth;
}
